import { faSolidMapCirclePlus } from '@awesome.me/kit-09b03e5df4/icons/kit/custom';
import { css } from '@emotion/react';
import {
  type ReactNode, useMemo,
} from 'react';
import { Trans } from 'react-i18next';
import {
  ButtonComponent, ButtonStyle,
} from '~/_shared/baseComponents/buttons';
import { Stack } from '~/_shared/baseComponents/layout/stack.component';
import { useCreateNewMap } from '~/_shared/components/prepareDataModal/useCreateNewMap';
import { useTranslation } from '~/_shared/utils/hooks';
import { BulletItem } from './bulletItem.component';
import { MapTemplates } from './mapTemplates.helpers';

const createMapButtonStyle = css({ alignSelf: 'flex-start' });

const transComponents = { bullet: <BulletItem /> };

export type TemplateInstructions = Readonly<{
  heading: string;
  steps: ReadonlyArray<ReactNode>;
  note?: string;
}>;

export const useMapTemplateInstructions = (): Partial<Record<MapTemplates, TemplateInstructions>> => {
  const [t] = useTranslation();
  const { onCreateNewMap } = useCreateNewMap();

  return useMemo(() => ({
    [MapTemplates.Grouping]: {
      heading: t('MapTemplates.Grouping.title'),
      steps: [
        t('MapTemplates.Grouping.tutorial.step1'),
        <Trans
          key="step2"
          i18nKey="MapTemplates.Grouping.tutorial.step2"
          components={transComponents}
        />,
        t('MapTemplates.Grouping.tutorial.step3'),
      ],
    },
    [MapTemplates.Radii]: {
      heading: t('MapTemplates.Radii.title'),
      steps: [
        t('MapTemplates.Radii.tutorial.step1'),
        t('MapTemplates.Radii.tutorial.step2'),
      ],
    },
    [MapTemplates.DTP]: {
      heading: t('MapTemplates.DTP.title'),
      steps: [
        t('MapTemplates.DTP.tutorial.step1'),
        t('MapTemplates.DTP.tutorial.step2'),
      ],
    },
    [MapTemplates.Heatmaps]: {
      heading: t('MapTemplates.Heatmaps.title'),
      steps: [
        <Trans
          key="step1"
          i18nKey="MapTemplates.Heatmaps.tutorial.step1"
          components={transComponents}
        />,
      ],
    },
    [MapTemplates.BoundariesPlain]: {
      heading: t('MapTemplates.BoundariesPlain.title'),
      steps: [
        t('MapTemplates.BoundariesPlain.tutorial.step1'),
      ],
    },
    [MapTemplates.BoundariesDemographic]: {
      heading: t('MapTemplates.BoundariesDemographic.title'),
      steps: [
        t('MapTemplates.BoundariesDemographic.tutorial.step1'),
      ],
      note: t('MapTemplates.BoundariesDemographic.tutorial.note'),
    },
    [MapTemplates.BoundariesFill]: {
      heading: t('MapTemplates.BoundariesFill.title'),
      steps: [
        t('MapTemplates.BoundariesFill.tutorial.step1'),
      ],
    },
    [MapTemplates.Routes]: {
      heading: t('MapTemplates.Routes.title'),
      steps: [
        t('MapTemplates.Routes.tutorial.step1'),
        t('MapTemplates.Routes.tutorial.step2'),
        t('MapTemplates.Routes.tutorial.step3'),
      ],
    },
    [MapTemplates.TerritoryManual]: {
      heading: t('MapTemplates.TerritoryManual.title'),
      steps: [
        t('MapTemplates.TerritoryManual.tutorial.step1'),
        t('MapTemplates.TerritoryManual.tutorial.step2'),
      ],
    },
    [MapTemplates.POI]: {
      heading: t('MapTemplates.POI.title'),
      steps: [
        t('MapTemplates.POI.tutorial.step1'),
        t('MapTemplates.POI.tutorial.step2'),
        t('MapTemplates.POI.tutorial.step3'),
      ],
    },
    [MapTemplates.Layering]: {
      heading: t('MapTemplates.Layering.title'),
      steps: [
        <Stack
          gap={4}
          key="MapTemplates.Layering.tutorial.step1"
        >
          {t('MapTemplates.Layering.tutorial.step1')}
          <ButtonComponent
            css={createMapButtonStyle}
            prefixIcon={faSolidMapCirclePlus}
            text={t('Create New Map')}
            buttonStyle={ButtonStyle.Success}
            onClick={() => onCreateNewMap()}
          />
        </Stack>,
      ],
    },
    [MapTemplates.Styling]: {
      heading: t('MapTemplates.Styling.title'),
      steps: [
        t('MapTemplates.Styling.tutorial.step1'),
        t('MapTemplates.Styling.tutorial.step2'),
        t('MapTemplates.Styling.tutorial.step3'),
      ],
    },
  }), [onCreateNewMap, t]);
};
